import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { logActivity, ACTIVITY_TYPES } from './activityService';
import { openIndexedDBWithStores } from './indexedDBUtils';
import { conditionalLog } from './consoleLogger';

/**
 * Comprehensive Blacklist Service for STB Workshop Management
 * Handles blacklist incidents and client blacklist status management
 */

// ===== BLACKLIST INCIDENT CRUD OPERATIONS =====

/**
 * Creates a new blacklist incident
 * @param {Object} incidentData - Incident data object
 * @returns {Promise<Object>} Created incident with ID
 */
export const createBlacklistIncident = async (incidentData) => {
  try {
    // Validate required fields
    if (!incidentData.clienteId || !incidentData.ordenFolio || !incidentData.motivoIncidente || !incidentData.detallesIncidente) {
      throw new Error('Cliente, orden, motivo y detalles son campos obligatorios');
    }

    // Validate fecha_incidente is not in the future
    const fechaIncidente = incidentData.fechaIncidente ? 
      (incidentData.fechaIncidente instanceof Date ? incidentData.fechaIncidente : incidentData.fechaIncidente.toDate()) :
      new Date();
    
    if (fechaIncidente > new Date()) {
      throw new Error('La fecha del incidente no puede ser futura');
    }

    // Validate monto_deuda if provided
    if (incidentData.montoDeuda && (isNaN(incidentData.montoDeuda) || incidentData.montoDeuda < 0)) {
      throw new Error('El monto de deuda debe ser un número positivo');
    }

    // Prepare incident data
    const newIncidentData = {
      clienteId: incidentData.clienteId,
      ordenFolio: incidentData.ordenFolio,
      fechaIncidente: incidentData.fechaIncidente ? 
        (incidentData.fechaIncidente instanceof Date ? Timestamp.fromDate(incidentData.fechaIncidente) : incidentData.fechaIncidente) :
        Timestamp.now(),
      motivoIncidente: incidentData.motivoIncidente.trim(),
      detallesIncidente: incidentData.detallesIncidente.trim(),
      montoDeuda: incidentData.montoDeuda || null,
      estado: 'activo',
      
      // Audit fields
      usuarioCreador: incidentData.usuarioCreador || 'system',
      fechaCreacion: Timestamp.now(),
      fechaArchivado: null,
      usuarioArchivador: null
    };

    const docRef = await addDoc(collection(db, 'blacklist_incidents'), newIncidentData);
    
    const createdIncident = {
      id: docRef.id,
      ...newIncidentData
    };

    // Update client blacklist status
    await updateClientBlacklistStatus(incidentData.clienteId);

    // Log activity
    try {
      await logActivity(
        ACTIVITY_TYPES.CLIENT_BLACKLISTED,
        `Cliente agregado a lista negra: ${incidentData.motivoIncidente}`,
        {
          clienteId: incidentData.clienteId,
          incidentId: docRef.id,
          ordenFolio: incidentData.ordenFolio,
          motivo: incidentData.motivoIncidente
        }
      );
    } catch (error) {
      console.error('Error logging blacklist activity:', error);
    }

    // Sync updated blacklist incidents to IndexedDB cache for immediate UI updates
    try {
      await syncBlacklistIncidentsFromFirestore();
    } catch (error) {
      console.error('Error syncing blacklist incidents to cache:', error);
      // Don't fail the operation if cache sync fails
    }

    return createdIncident;
  } catch (error) {
    console.error('Error creating blacklist incident:', error);
    throw new Error(error.message || 'Error al crear el incidente de lista negra');
  }
};

/**
 * Gets all incidents for a specific client
 * @param {string} clienteId - Client ID
 * @returns {Promise<Array>} Array of incidents
 */
export const getClientIncidents = async (clienteId) => {
  try {
    const q = query(
      collection(db, 'blacklist_incidents'),
      where('clienteId', '==', clienteId)
    );
    
    const querySnapshot = await getDocs(q);
    const incidents = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by creation date in JavaScript (newest first)
    incidents.sort((a, b) => {
      const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
      const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    return incidents;
  } catch (error) {
    console.error('Error fetching client incidents:', error);
    return [];
  }
};

/**
 * Gets all active blacklist incidents
 * @returns {Promise<Array>} Array of active incidents
 */
export const getActiveIncidents = async () => {
  try {
    const q = query(
      collection(db, 'blacklist_incidents'),
      where('estado', '==', 'activo')
    );
    
    const querySnapshot = await getDocs(q);
    const incidents = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by creation date in JavaScript (newest first)
    incidents.sort((a, b) => {
      const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
      const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    return incidents;
  } catch (error) {
    console.error('Error fetching active incidents:', error);
    return [];
  }
};

/**
 * Gets all blacklist incidents (active and archived)
 * @returns {Promise<Array>} Array of all incidents
 */
export const getAllIncidents = async () => {
  try {
    const q = query(collection(db, 'blacklist_incidents'));
    
    const querySnapshot = await getDocs(q);
    const incidents = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort by creation date in JavaScript (newest first)
    incidents.sort((a, b) => {
      const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
      const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    return incidents;
  } catch (error) {
    console.error('Error fetching all incidents:', error);
    return [];
  }
};

/**
 * Archives a blacklist incident
 * @param {string} incidentId - Incident ID
 * @param {string} usuarioArchivador - User who is archiving the incident
 * @returns {Promise<boolean>} Success status
 */
export const archiveIncident = async (incidentId, usuarioArchivador = 'system') => {
  try {
    // Get incident data first
    const incidentDoc = await getDoc(doc(db, 'blacklist_incidents', incidentId));
    if (!incidentDoc.exists()) {
      throw new Error('Incidente no encontrado');
    }

    const incidentData = incidentDoc.data();
    
    // Update incident to archived
    await updateDoc(doc(db, 'blacklist_incidents', incidentId), {
      estado: 'archivado',
      fechaArchivado: Timestamp.now(),
      usuarioArchivador: usuarioArchivador
    });

    // Update client blacklist status
    await updateClientBlacklistStatus(incidentData.clienteId);

    // Log activity
    try {
      await logActivity(
        ACTIVITY_TYPES.CLIENT_UPDATED,
        `Incidente de lista negra archivado: ${incidentData.motivoIncidente}`,
        {
          clienteId: incidentData.clienteId,
          incidentId: incidentId,
          ordenFolio: incidentData.ordenFolio
        }
      );
    } catch (error) {
      console.error('Error logging archive activity:', error);
    }

    // Sync updated blacklist incidents to IndexedDB cache for immediate UI updates
    try {
      await syncBlacklistIncidentsFromFirestore();
    } catch (error) {
      console.error('Error syncing blacklist incidents to cache:', error);
      // Don't fail the operation if cache sync fails
    }

    return true;
  } catch (error) {
    console.error('Error archiving incident:', error);
    throw new Error(error.message || 'Error al archivar el incidente');
  }
};

/**
 * Updates an existing blacklist incident
 * @param {string} incidentId - Incident ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<boolean>} Success status
 */
export const updateIncident = async (incidentId, updateData) => {
  try {
    // Validate fecha_incidente is not in the future if provided
    if (updateData.fechaIncidente) {
      const fechaIncidente = updateData.fechaIncidente instanceof Date ? 
        updateData.fechaIncidente : updateData.fechaIncidente.toDate();
      
      if (fechaIncidente > new Date()) {
        throw new Error('La fecha del incidente no puede ser futura');
      }
    }

    // Validate monto_deuda if provided
    if (updateData.montoDeuda && (isNaN(updateData.montoDeuda) || updateData.montoDeuda < 0)) {
      throw new Error('El monto de deuda debe ser un número positivo');
    }

    // Prepare update data
    const processedUpdateData = {
      ...updateData,
      fechaUltimaModificacion: Timestamp.now()
    };

    // Convert fecha to Timestamp if it's a Date
    if (processedUpdateData.fechaIncidente instanceof Date) {
      processedUpdateData.fechaIncidente = Timestamp.fromDate(processedUpdateData.fechaIncidente);
    }

    await updateDoc(doc(db, 'blacklist_incidents', incidentId), processedUpdateData);

    return true;
  } catch (error) {
    console.error('Error updating incident:', error);
    throw new Error(error.message || 'Error al actualizar el incidente');
  }
};

// ===== CLIENT BLACKLIST STATUS MANAGEMENT =====

/**
 * Updates a client's blacklist status based on their active incidents
 * @param {string} clienteId - Client ID
 * @returns {Promise<Object>} Updated blacklist status
 */
export const updateClientBlacklistStatus = async (clienteId) => {
  try {
    // Get all active incidents for this client
    const activeIncidentsQuery = query(
      collection(db, 'blacklist_incidents'),
      where('clienteId', '==', clienteId),
      where('estado', '==', 'activo')
    );
    
    const activeSnapshot = await getDocs(activeIncidentsQuery);
    const activeIncidents = activeSnapshot.docs;

    // Calculate blacklist status
    const isBlacklisted = activeIncidents.length > 0;
    const activeIncidentsCount = activeIncidents.length;

    // Get the most recent incident for tooltip
    let mostRecentIncident = null;
    if (activeIncidents.length > 0) {
      const sortedIncidents = activeIncidents
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
          const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
          return dateB - dateA;
        });
      
      mostRecentIncident = sortedIncidents[0];
    }

    // Update client document with blacklist status
    try {
      await updateDoc(doc(db, 'clients', clienteId), {
        isBlacklisted: isBlacklisted,
        activeIncidentsCount: activeIncidentsCount,
        mostRecentIncidentMotivo: mostRecentIncident ? mostRecentIncident.motivoIncidente : null,
        blacklistUpdatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating client blacklist status:', error);
      // Don't throw error if client update fails, incident was still processed
    }

    return {
      isBlacklisted,
      activeIncidentsCount,
      mostRecentIncident
    };
  } catch (error) {
    console.error('Error updating client blacklist status:', error);
    return {
      isBlacklisted: false,
      activeIncidentsCount: 0,
      mostRecentIncident: null
    };
  }
};

/**
 * Checks if a client is currently blacklisted
 * @param {string} clienteId - Client ID
 * @returns {Promise<Object>} Blacklist status information
 */
export const checkClientBlacklistStatus = async (clienteId) => {
  try {
    // Get client document to check cached status
    const clientDoc = await getDoc(doc(db, 'clients', clienteId));
    if (!clientDoc.exists()) {
      return { isBlacklisted: false, activeIncidentsCount: 0, mostRecentIncident: null };
    }

    const clientData = clientDoc.data();
    
    // Return cached status if available and recent, but we still need to get the full incident object
    if (clientData.isBlacklisted !== undefined && clientData.blacklistUpdatedAt) {
      const lastUpdated = clientData.blacklistUpdatedAt.toDate();
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      
      
      if (lastUpdated > oneHourAgo && clientData.isBlacklisted && clientData.mostRecentIncidentMotivo) {
        // For blacklisted clients with complete cached data, we still need to get the full incident object for navigation
        try {
          const activeIncidentsQuery = query(
            collection(db, 'blacklist_incidents'),
            where('clienteId', '==', clienteId),
            where('estado', '==', 'activo')
          );
          
          const activeSnapshot = await getDocs(activeIncidentsQuery);
          let mostRecentIncident = null;
          
          if (activeSnapshot.docs.length > 0) {
            const sortedIncidents = activeSnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .sort((a, b) => {
                const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
                const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
                return dateB - dateA;
              });
            
            mostRecentIncident = sortedIncidents[0];
          }
          
          return {
            isBlacklisted: clientData.isBlacklisted || false,
            activeIncidentsCount: clientData.activeIncidentsCount || 0,
            mostRecentIncidentMotivo: clientData.mostRecentIncidentMotivo || null,
            mostRecentIncident
          };
        } catch (error) {
          console.error('Error fetching most recent incident:', error);
          // Fallback to cached data without incident object
          return {
            isBlacklisted: clientData.isBlacklisted || false,
            activeIncidentsCount: clientData.activeIncidentsCount || 0,
            mostRecentIncidentMotivo: clientData.mostRecentIncidentMotivo || null,
            mostRecentIncident: null
          };
        }
      } else if (lastUpdated > oneHourAgo && !clientData.isBlacklisted) {
        // For non-blacklisted clients, no need to fetch incidents
        return {
          isBlacklisted: false,
          activeIncidentsCount: 0,
          mostRecentIncidentMotivo: null,
          mostRecentIncident: null
        };
      }
    }

    // Fallback to real-time check - get fresh data from Firestore
    const updatedStatus = await updateClientBlacklistStatus(clienteId);
    
    // The updateClientBlacklistStatus function returns { isBlacklisted, activeIncidentsCount, mostRecentIncident }
    // But we need to also return mostRecentIncidentMotivo for the UI
    return {
      isBlacklisted: updatedStatus.isBlacklisted,
      activeIncidentsCount: updatedStatus.activeIncidentsCount,
      mostRecentIncidentMotivo: updatedStatus.mostRecentIncident ? updatedStatus.mostRecentIncident.motivoIncidente : null,
      mostRecentIncident: updatedStatus.mostRecentIncident
    };
  } catch (error) {
    console.error('Error checking client blacklist status:', error);
    return { isBlacklisted: false, activeIncidentsCount: 0, mostRecentIncident: null };
  }
};

/**
 * Gets blacklist information for multiple clients efficiently
 * @param {Array} clientIds - Array of client IDs
 * @returns {Promise<Object>} Map of clientId to blacklist status
 */
/**
 * Gets blacklist status from IndexedDB cache (optimized version)
 * @param {Array} clientIds - Array of client IDs
 * @returns {Promise<Object>} Map of client blacklist status
 */
/**
 * Ensures the blacklist_incidents store exists in IndexedDB
 * @returns {Promise<boolean>} Success status
 */
const ensureBlacklistStore = async () => {
  try {
    return new Promise((resolve, reject) => {
      // First, check current version and whether store exists
      const checkRequest = indexedDB.open('STBWorkshop', 23);
      checkRequest.onsuccess = () => {
        const db = checkRequest.result;
        const currentVersion = db.version;
        const hasBlacklistStore = db.objectStoreNames.contains('blacklist_incidents');
        
        conditionalLog(`Current IndexedDB version: ${currentVersion}, has blacklist store: ${hasBlacklistStore}`);
        
        if (hasBlacklistStore) {
          // Store already exists, no need to upgrade
          db.close();
          resolve(true);
          return;
        }
        
        db.close();
        
        // Store doesn't exist, we need to upgrade
        const upgradeRequest = indexedDB.open('STBWorkshop', 23);
        
        upgradeRequest.onerror = () => {
          console.error('Error opening IndexedDB for blacklist store:', upgradeRequest.error);
          resolve(false);
        };
        
        upgradeRequest.onupgradeneeded = (event) => {
          const db = event.target.result;
          conditionalLog(`Upgrading IndexedDB to version ${db.version} to add blacklist store`);
          
          if (!db.objectStoreNames.contains('blacklist_incidents')) {
            conditionalLog('Creating blacklist_incidents object store');
            db.createObjectStore('blacklist_incidents', { keyPath: 'id' });
          }
        };
        
        upgradeRequest.onsuccess = () => {
          const db = upgradeRequest.result;
          conditionalLog(`Successfully opened IndexedDB version ${db.version}`);
          conditionalLog('Available stores:', Array.from(db.objectStoreNames));
          db.close();
          resolve(true);
        };
      };
      
      checkRequest.onerror = () => {
        console.error('Error checking IndexedDB version:', checkRequest.error);
        resolve(false);
      };
    });
  } catch (error) {
    console.error('Error ensuring blacklist store:', error);
    return false;
  }
};

/**
 * Gets blacklist incidents from IndexedDB cache
 * @returns {Promise<Array>} Array of blacklist incidents
 */
const getBlacklistIncidentsFromCache = async () => {
  try {
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve, reject) => {
      
      // Database already opened, proceed with transaction
        conditionalLog('Reading from IndexedDB, available stores:', Array.from(db.objectStoreNames));
        
        if (!db.objectStoreNames.contains('blacklist_incidents')) {
          conditionalLog('blacklist_incidents store not found, returning empty array');
          db.close();
          resolve([]);
          return;
        }
        
        const transaction = db.transaction(['blacklist_incidents'], 'readonly');
        const store = transaction.objectStore('blacklist_incidents');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          const incidents = getAllRequest.result || [];
          conditionalLog(`Raw IndexedDB incidents: ${incidents.length}`);
          if (incidents.length > 0) {
            conditionalLog('Sample raw IndexedDB incident:', incidents[0]);
          }
          
          // Deserialize dates from ISO strings back to Date objects
          const deserializedIncidents = incidents.map(incident => ({
            ...incident,
            fechaIncidente: incident.fechaIncidente ? new Date(incident.fechaIncidente) : null,
            fechaCreacion: incident.fechaCreacion ? new Date(incident.fechaCreacion) : null,
            fechaResolucion: incident.fechaResolucion ? new Date(incident.fechaResolucion) : null,
            fechaArchivado: incident.fechaArchivado ? new Date(incident.fechaArchivado) : null
          }));
          
          conditionalLog(`Deserialized IndexedDB incidents: ${deserializedIncidents.length}`);
          db.close();
          resolve(deserializedIncidents);
        };
        
        getAllRequest.onerror = () => {
          console.error('Error getting blacklist incidents:', getAllRequest.error);
          db.close();
          resolve([]);
        };
        
        transaction.onerror = () => {
          console.error('Transaction error:', transaction.error);
          db.close();
          resolve([]);
        };
    });
  } catch (error) {
    console.error('Error getting blacklist incidents from cache:', error);
    return [];
  }
};

/**
 * Syncs blacklist incidents from Firestore to IndexedDB
 * @returns {Promise<void>}
 */
const syncBlacklistIncidentsFromFirestore = async () => {
  try {
    // Fetch all blacklist incidents from Firestore
    const blacklistQuery = query(collection(db, 'blacklist_incidents'));
    const blacklistSnapshot = await getDocs(blacklistQuery);
    
    const incidents = blacklistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    conditionalLog(`Fetched ${incidents.length} blacklist incidents from Firestore`);
    if (incidents.length > 0) {
      conditionalLog('Sample Firestore incident:', incidents[0]);
    }
    
    // Sync to IndexedDB and wait for completion
    await syncBlacklistToIndexedDB(incidents);
    conditionalLog(`Synced ${incidents.length} blacklist incidents to cache`);
    
    // Add a small delay to ensure IndexedDB transaction completes
    await new Promise(resolve => setTimeout(resolve, 100));
  } catch (error) {
    console.error('Error syncing blacklist incidents from Firestore:', error);
    // Don't throw - this is optional for performance
  }
};

export const getBulkClientBlacklistStatusFromCache = async (clientIds, retryCount = 0) => {
  try {
    if (!clientIds || clientIds.length === 0) {
      return {};
    }

    // Get blacklist incidents from IndexedDB cache
    const cachedIncidents = await getBlacklistIncidentsFromCache();
    conditionalLog(`Found ${cachedIncidents.length} cached blacklist incidents`);
    
    // If no cached incidents and we haven't retried yet, sync from Firestore
    if (cachedIncidents.length === 0 && retryCount === 0) {
      conditionalLog('No cached blacklist incidents, syncing from Firestore...');
      await syncBlacklistIncidentsFromFirestore();
      // Get the incidents again after sync
      const refreshedIncidents = await getBlacklistIncidentsFromCache();
      conditionalLog(`After sync: found ${refreshedIncidents.length} blacklist incidents`);
      
      // If we still have no incidents after sync, something is wrong with the data
      if (refreshedIncidents.length === 0) {
        console.warn('No blacklist incidents found in Firestore either');
        return {};
      }
      
      // Retry once with fresh data
      return getBulkClientBlacklistStatusFromCache(clientIds, 1);
    }
    
    if (cachedIncidents.length > 0) {
      conditionalLog('Sample incident:', cachedIncidents[0]);
    }
    
    const statusMap = {};
    
    // Process each client
    clientIds.forEach(clientId => {
      // Find active incidents for this client
      const clientActiveIncidents = cachedIncidents.filter(incident => 
        incident.clienteId === clientId && incident.estado === 'activo'
      );
      
      conditionalLog(`Client ${clientId}: found ${clientActiveIncidents.length} active incidents`);
      
      const isBlacklisted = clientActiveIncidents.length > 0;
      const activeIncidentsCount = clientActiveIncidents.length;
      
      // Get most recent incident motivo
      let mostRecentIncidentMotivo = null;
      if (clientActiveIncidents.length > 0) {
        const sortedIncidents = clientActiveIncidents.sort((a, b) => {
          const dateA = a.fechaIncidente instanceof Date ? a.fechaIncidente : new Date(a.fechaIncidente || 0);
          const dateB = b.fechaIncidente instanceof Date ? b.fechaIncidente : new Date(b.fechaIncidente || 0);
          return dateB - dateA;
        });
        mostRecentIncidentMotivo = sortedIncidents[0].motivoIncidente;
      }
      
      statusMap[clientId] = {
        isBlacklisted,
        activeIncidentsCount,
        mostRecentIncidentMotivo
      };
      
      if (isBlacklisted) {
        conditionalLog(`Client ${clientId} is blacklisted with ${activeIncidentsCount} incidents`);
      }
    });
    
    return statusMap;
  } catch (error) {
    console.error('Error getting cached blacklist status:', error);
    return {};
  }
};

export const getBulkClientBlacklistStatus = async (clientIds) => {
  try {
    if (!clientIds || clientIds.length === 0) {
      return {};
    }

    // Process clients in batches of 10 due to Firestore 'in' query limit
    const statusMap = {};
    const batchSize = 10;
    
    for (let i = 0; i < clientIds.length; i += batchSize) {
      const batch = clientIds.slice(i, i + batchSize);
      
      // Get all active incidents for this batch of clients
      const activeIncidentsQuery = query(
        collection(db, 'blacklist_incidents'),
        where('clienteId', 'in', batch),
        where('estado', '==', 'activo')
      );
      
      const activeSnapshot = await getDocs(activeIncidentsQuery);
      
      // Group incidents by client
      const clientIncidents = {};
      activeSnapshot.docs.forEach(doc => {
        const incident = { id: doc.id, ...doc.data() };
        if (!clientIncidents[incident.clienteId]) {
          clientIncidents[incident.clienteId] = [];
        }
        clientIncidents[incident.clienteId].push(incident);
      });

      // Build status map for this batch
      batch.forEach(clientId => {
        const incidents = clientIncidents[clientId] || [];
        const isBlacklisted = incidents.length > 0;
        
        let mostRecentIncident = null;
        if (incidents.length > 0) {
          const sorted = incidents.sort((a, b) => {
            const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
            const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
            return dateB - dateA;
          });
          mostRecentIncident = sorted[0];
        }

        statusMap[clientId] = {
          isBlacklisted,
          activeIncidentsCount: incidents.length,
          mostRecentIncidentMotivo: mostRecentIncident ? mostRecentIncident.motivoIncidente : null
        };
      });
    }

    return statusMap;
  } catch (error) {
    console.error('Error getting bulk client blacklist status:', error);
    
    // Return empty status for all clients on error
    const statusMap = {};
    clientIds.forEach(clientId => {
      statusMap[clientId] = { isBlacklisted: false, activeIncidentsCount: 0, mostRecentIncident: null };
    });
    return statusMap;
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Formats date to Spanish format (DD/MMM/YYYY)
 * @param {Date|Timestamp} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatIncidentDate = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : date.toDate();
  const months = [
    'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
    'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
  ];
  
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = months[dateObj.getUTCMonth()];
  const year = dateObj.getUTCFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Gets CSS class for blacklist incident status
 * @param {string} estado - Incident status
 * @returns {string} CSS class name
 */
export const getIncidentStatusColor = (estado) => {
  const statusColors = {
    'activo': 'bg-red-100 text-red-800',
    'archivado': 'bg-gray-100 text-gray-800'
  };
  
  return statusColors[estado] || 'bg-gray-100 text-gray-800';
};

/**
 * Validates incident data before creation/update
 * @param {Object} incidentData - Incident data to validate
 * @returns {Object} Validation result { isValid: boolean, errors: Array }
 */
export const validateIncidentData = (incidentData) => {
  const errors = [];

  if (!incidentData.clienteId?.trim()) {
    errors.push('Cliente es requerido');
  }

  if (!incidentData.ordenFolio || !String(incidentData.ordenFolio).trim()) {
    errors.push('Folio de orden es requerido');
  }

  if (!incidentData.motivoIncidente?.trim()) {
    errors.push('Motivo del incidente es requerido');
  }

  if (!incidentData.detallesIncidente?.trim()) {
    errors.push('Detalles del incidente son requeridos');
  }

  if (incidentData.fechaIncidente) {
    const fechaIncidente = incidentData.fechaIncidente instanceof Date ? 
      incidentData.fechaIncidente : new Date(incidentData.fechaIncidente);
    
    if (fechaIncidente > new Date()) {
      errors.push('La fecha del incidente no puede ser futura');
    }
  }

  if (incidentData.montoDeuda && (isNaN(incidentData.montoDeuda) || incidentData.montoDeuda < 0)) {
    errors.push('El monto de deuda debe ser un número positivo');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Permanently deletes a blacklist incident
 * @param {string} incidentId - Incident ID to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteIncident = async (incidentId) => {
  try {
    // Get incident data before deletion for activity logging
    const incidentDoc = await getDoc(doc(db, 'blacklist_incidents', incidentId));
    
    if (!incidentDoc.exists()) {
      throw new Error('Incidente no encontrado');
    }

    const incidentData = incidentDoc.data();
    
    // Delete the incident document
    await deleteDoc(doc(db, 'blacklist_incidents', incidentId));

    // Update client blacklist status after deletion
    if (incidentData.clienteId) {
      try {
        await updateClientBlacklistStatus(incidentData.clienteId);
      } catch (error) {
        console.error('Error updating client blacklist status after deletion:', error);
        // Continue even if status update fails
      }
    }

    // Log activity
    try {
      await logActivity(
        ACTIVITY_TYPES.CLIENT_BLACKLIST_REMOVED,
        `Incidente de lista negra eliminado: ${incidentData.motivoIncidente}`,
        {
          clienteId: incidentData.clienteId,
          incidentId,
          ordenFolio: incidentData.ordenFolio,
          motivo: incidentData.motivoIncidente
        }
      );
    } catch (error) {
      console.error('Error logging delete activity:', error);
      // Continue even if activity logging fails
    }

    // Sync updated blacklist incidents to IndexedDB cache for immediate UI updates
    try {
      await syncBlacklistIncidentsFromFirestore();
    } catch (error) {
      console.error('Error syncing blacklist incidents to cache:', error);
      // Don't fail the operation if cache sync fails
    }

    return true;
  } catch (error) {
    console.error('Error deleting incident:', error);
    throw new Error(error.message || 'Error al eliminar el incidente');
  }
};

/**
 * Syncs blacklist incidents to IndexedDB for local access
 * @param {Array} incidents - Blacklist incidents to sync
 * @returns {Promise<boolean>} Success status
 */
export const syncBlacklistToIndexedDB = async (incidents) => {
  try {
    // First ensure the store exists
    await ensureBlacklistStore();
    
    // Now open the database to sync data
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve, reject) => {
      
      // Database already opened, proceed with transaction
        conditionalLog('Opened IndexedDB for sync, available stores:', Array.from(db.objectStoreNames));
        
        if (!db.objectStoreNames.contains('blacklist_incidents')) {
          console.error('blacklist_incidents store not found after ensuring store exists');
          db.close();
          resolve(false);
          return;
        }
        
        const transaction = db.transaction(['blacklist_incidents'], 'readwrite');
        const store = transaction.objectStore('blacklist_incidents');
        
        // Clear existing data
        const clearRequest = store.clear();
        clearRequest.onsuccess = () => {
          conditionalLog('Cleared existing blacklist incidents from IndexedDB');
          
          // Add all incidents with proper date serialization
          let addedCount = 0;
          incidents.forEach((incident, index) => {
            // Serialize Firestore Timestamps to ISO strings for IndexedDB storage
            const serializedIncident = {
              ...incident,
              fechaIncidente: incident.fechaIncidente?.toDate ? incident.fechaIncidente.toDate().toISOString() : incident.fechaIncidente,
              fechaCreacion: incident.fechaCreacion?.toDate ? incident.fechaCreacion.toDate().toISOString() : incident.fechaCreacion,
              fechaResolucion: incident.fechaResolucion?.toDate ? incident.fechaResolucion.toDate().toISOString() : incident.fechaResolucion,
              fechaArchivado: incident.fechaArchivado?.toDate ? incident.fechaArchivado.toDate().toISOString() : incident.fechaArchivado
            };
            
            const putRequest = store.put(serializedIncident);
            putRequest.onsuccess = () => {
              addedCount++;
              if (addedCount === incidents.length) {
                conditionalLog(`Successfully added ${addedCount} blacklist incidents to IndexedDB`);
              }
            };
            putRequest.onerror = () => {
              console.error(`Error adding incident ${index}:`, putRequest.error);
            };
          });
        };
        
        transaction.oncomplete = () => {
          conditionalLog(`Transaction complete: synced ${incidents.length} blacklist incidents to IndexedDB`);
          db.close();
          resolve(true);
        };
        
        transaction.onerror = () => {
          console.error('IndexedDB transaction error:', transaction.error);
          db.close();
          resolve(false);
        };
    });
  } catch (error) {
    console.error('Error syncing blacklist to IndexedDB:', error);
    return false;
  }
};